import { CATEGORY_FAMILIES, SCORE_BANDS } from "@/services/intelligence/family-mapping.ts";
import { RAISE_BUCKETS } from "@/shared/intelligence/peer-cohort-format.ts";

export const SCORE_BAND_OPTIONS = SCORE_BANDS;
export const FAMILY_OPTIONS = CATEGORY_FAMILIES;
export const RAISE_BUCKET_OPTIONS = RAISE_BUCKETS.map((b) => b.label);

export const TREND_OPTIONS = ["up", "flat", "down"] as const;
export type TrendOption = (typeof TREND_OPTIONS)[number];

export const TOKEN_VERIFIED_OPTIONS = ["coingecko", "cmc", "defillama"] as const;
export type TokenVerifiedOption = (typeof TOKEN_VERIFIED_OPTIONS)[number];

// Mirrors actual distinct `projects.discovery_source` values in the
// live DB. Keep in lockstep with backend list in
// `src/services/leaderboard/filter-builder.ts`.
export const DISCOVERY_SOURCE_OPTIONS = [
  "rootdata",
  "moni_mindshare",
  "defillama",
  "airtable",
  "unknown",
] as const;
export type DiscoverySourceOption = (typeof DISCOVERY_SOURCE_OPTIONS)[number];

export const SUBSCORE_KEYS = [
  "social",
  "backers",
  "pedigree",
  "momentum",
  "dev",
  "revenue",
] as const;
export type SubScoreKey = (typeof SUBSCORE_KEYS)[number];

/**
 * Frontend mirror of the backend leaderboard filter schema. Every field
 * is optional; all-undefined === the unfiltered leaderboard.
 *
 * Keep in lockstep with `src/services/leaderboard/filter-builder.ts`.
 */
export interface LeaderboardFilters {
  search?: string;
  category?: string;
  archived?: boolean;
  scoreMin?: number;
  scoreMax?: number;
  scoreBand?: string[];
  family?: string[];
  raiseMin?: number;
  raiseMax?: number;
  raiseBucket?: string[];
  raiseUnknown?: boolean;
  trend?: TrendOption;
  hasBackers?: boolean;
  tgeConfidenceMin?: number;
  tgeConfidenceMax?: number;
  hasTgeHints?: boolean;
  hasToken?: boolean;
  tokenVerified?: TokenVerifiedOption[];
  ecosystem?: string[];
  auditedWithinDays?: number;
  auditedOlderThanDays?: number;
  discoverySource?: DiscoverySourceOption[];
  subscoreKey?: SubScoreKey;
  subscoreMin?: number;
}

export const EMPTY_FILTERS: LeaderboardFilters = {};

// ─── Quick-filter presets ───────────────────────────────────────────

export const QUICK_FILTER_IDS = [
  "score-40",
  "score-30",
  "trending-up",
  "trending-down",
  "sweet-spot",
  "protected-sales",
  "pre-tge",
  "has-token",
  "stealth",
  "mega-raise",
  "recently-audited",
  "stale-audit",
] as const;
export type QuickFilterId = (typeof QUICK_FILTER_IDS)[number];

export interface QuickFilterPreset {
  id: QuickFilterId;
  label: string;
  hint: string;
  filters: Partial<LeaderboardFilters>;
}

export const QUICK_FILTER_PRESETS: Record<QuickFilterId, QuickFilterPreset> = {
  "score-40": {
    id: "score-40",
    label: "Score 40+",
    hint: "Top scoring band",
    filters: { scoreBand: ["40+"] },
  },
  "score-30": {
    id: "score-30",
    label: "Score 30+",
    hint: "Above-average scoring",
    filters: { scoreBand: ["30-39", "40+"] },
  },
  "trending-up": {
    id: "trending-up",
    label: "Trending up",
    hint: "Rank moved up over the last 7 days",
    filters: { trend: "up" },
  },
  "trending-down": {
    id: "trending-down",
    label: "Trending down",
    hint: "Rank dropped over the last 7 days",
    filters: { trend: "down" },
  },
  "sweet-spot": {
    id: "sweet-spot",
    label: "Sweet spot",
    hint: "Score 30+, raised $1–20M",
    filters: { scoreMin: 30, raiseMin: 1_000_000, raiseMax: 20_000_000 },
  },
  "protected-sales": {
    id: "protected-sales",
    label: "Protected sales",
    hint: "Raised under $3M with strong momentum (≥10)",
    filters: {
      raiseMax: 3_000_000,
      subscoreKey: "momentum",
      subscoreMin: 10,
    },
  },
  "pre-tge": {
    id: "pre-tge",
    label: "Pre-TGE high confidence",
    hint: "TGE confidence ≥ 50, no live token",
    filters: { tgeConfidenceMin: 50, hasToken: false },
  },
  "has-token": {
    id: "has-token",
    label: "Token detection high",
    // Leaderboard is pre-TGE focused; this chip is a triage signal
    // for projects that may have already TGE'd but haven't been
    // archived yet (manual-review band: 80–94% confidence, or
    // verified on one exchange but lacking the 2-source corroboration
    // that auto-archives at 95%+).
    hint: "Triage: token detection confidence ≥ 95 or verified on CoinGecko / CMC",
    filters: { hasToken: true },
  },
  stealth: {
    id: "stealth",
    label: "Stealth / undisclosed",
    hint: "No disclosed raise",
    filters: { raiseUnknown: true },
  },
  "mega-raise": {
    id: "mega-raise",
    label: "Mega-raise",
    hint: "Raised $50M+",
    filters: { raiseMin: 50_000_000 },
  },
  "recently-audited": {
    id: "recently-audited",
    label: "Recently audited",
    hint: "Audited within last 7 days",
    filters: { auditedWithinDays: 7 },
  },
  "stale-audit": {
    id: "stale-audit",
    label: "Stale audit",
    hint: "Audit older than 30 days",
    filters: { auditedOlderThanDays: 30 },
  },
};

// ─── URL param serialization ────────────────────────────────────────

/**
 * Serialize a filter object into URL query-param entries. Skips
 * undefined / empty values so the produced query string matches what
 * the backend Zod schema is expecting (omitted keys → undefined).
 */
export function filtersToQueryParams(filters: LeaderboardFilters): Record<string, string> {
  const out: Record<string, string> = {};
  const set = (key: string, value: unknown) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      if (value.length > 0) out[key] = value.join(",");
      return;
    }
    if (typeof value === "boolean") {
      if (value) out[key] = "true";
      return;
    }
    if (typeof value === "string") {
      if (value.trim()) out[key] = value;
      return;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
      out[key] = String(value);
    }
  };

  // hasToken is tri-state on the backend (missing / true / false).
  // Other booleans are toggle-only (true / missing) — keep the
  // shorthand encoding for those.
  if (filters.hasToken === true) out.has_token = "true";
  else if (filters.hasToken === false) out.has_token = "false";

  set("search", filters.search);
  set("category", filters.category);
  set("archived", filters.archived);
  set("score_min", filters.scoreMin);
  set("score_max", filters.scoreMax);
  set("score_band", filters.scoreBand);
  set("family", filters.family);
  set("raise_min", filters.raiseMin);
  set("raise_max", filters.raiseMax);
  set("raise_bucket", filters.raiseBucket);
  set("raise_unknown", filters.raiseUnknown);
  set("trend", filters.trend);
  set("has_backers", filters.hasBackers);
  set("tge_confidence_min", filters.tgeConfidenceMin);
  set("tge_confidence_max", filters.tgeConfidenceMax);
  set("has_tge_hints", filters.hasTgeHints);
  // has_token handled explicitly above (tri-state).
  set("token_verified", filters.tokenVerified);
  set("ecosystem", filters.ecosystem);
  set("audited_within_days", filters.auditedWithinDays);
  set("audited_older_than_days", filters.auditedOlderThanDays);
  set("discovery_source", filters.discoverySource);
  set("subscore_key", filters.subscoreKey);
  set("subscore_min", filters.subscoreMin);

  return out;
}

// ─── Active-filter accounting ───────────────────────────────────────

/**
 * Count how many filter axes are actually applied. Used for the
 * "X filters active" badge and for the Reset-All visibility gate.
 */
export function countActiveFilters(filters: LeaderboardFilters): number {
  let n = 0;
  if (filters.search?.trim()) n++;
  if (filters.category) n++;
  if (filters.archived) n++;
  if (filters.scoreMin != null) n++;
  if (filters.scoreMax != null) n++;
  if (filters.scoreBand && filters.scoreBand.length > 0) n++;
  if (filters.family && filters.family.length > 0) n++;
  if (filters.raiseMin != null) n++;
  if (filters.raiseMax != null) n++;
  if (filters.raiseBucket && filters.raiseBucket.length > 0) n++;
  if (filters.raiseUnknown) n++;
  if (filters.trend) n++;
  if (filters.hasBackers) n++;
  if (filters.tgeConfidenceMin != null) n++;
  if (filters.tgeConfidenceMax != null) n++;
  if (filters.hasTgeHints) n++;
  if (filters.hasToken != null) n++;
  if (filters.tokenVerified && filters.tokenVerified.length > 0) n++;
  if (filters.ecosystem && filters.ecosystem.length > 0) n++;
  if (filters.auditedWithinDays != null) n++;
  if (filters.auditedOlderThanDays != null) n++;
  if (filters.discoverySource && filters.discoverySource.length > 0) n++;
  if (filters.subscoreKey && filters.subscoreMin != null) n++;
  return n;
}

/**
 * Check whether the given filter object equals a quick-filter preset's
 * filter bundle (used to highlight which chip is active).
 *
 * `search` is intentionally ignored: presets preserve the search box
 * across switches by design, so a user with text in the search box can
 * still see which preset is active.
 */
export function matchQuickFilter(filters: LeaderboardFilters, preset: QuickFilterPreset): boolean {
  const presetEntries = Object.entries(preset.filters);
  const filtersEntries = Object.entries(filters).filter(([k, v]) => {
    if (k === "search") return false;
    if (v == null) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "string") return v.trim().length > 0;
    return true;
  });
  if (presetEntries.length !== filtersEntries.length) return false;
  for (const [key, value] of presetEntries) {
    const have = (filters as Record<string, unknown>)[key];
    if (Array.isArray(value)) {
      if (!Array.isArray(have)) return false;
      if (value.length !== have.length) return false;
      for (const v of value) if (!have.includes(v)) return false;
    } else if (have !== value) {
      return false;
    }
  }
  return true;
}
